import { fr, enUS, es } from 'date-fns/locale';
import type { Locale } from 'date-fns';

/**
 * Map a UI language code to a date-fns Locale object for use with
 * react-day-picker's `locale` prop. Defaults to fr when the language
 * is missing or unsupported.
 */
export function getDateFnsLocale(language: string | undefined): Locale {
  switch (language) {
    case 'en':
      return enUS;
    case 'es':
      return es;
    case 'fr':
    default:
      return fr;
  }
}

const LONG_MONTHS_BY_LANG: Record<string, string[]> = {
  fr: ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'],
  en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
  es: ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'],
};

const SHORT_MONTHS_BY_LANG: Record<string, string[]> = {
  fr: ['janv', 'févr', 'mars', 'avr', 'mai', 'juin', 'juil', 'août', 'sept', 'oct', 'nov', 'déc'],
  en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  es: ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'],
};

// Parse date in local time to avoid UTC timezone shift
function parseDateLocal(v: any): Date | undefined {
  if (v instanceof Date) return v;
  if (typeof v === 'string') {
    const m = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    const d = new Date(v);
    return isNaN(d.getTime()) ? undefined : d;
  }
  return undefined;
}

/**
 * Long-form localized date (e.g., "15 janvier 2024" / "January 15, 2024" / "15 enero 2024").
 * Accepts Date objects or ISO/date strings from DB. Defaults to fr.
 */
export function formatDateLocalized(date: Date | string, language: string = 'fr'): string {
  const months = LONG_MONTHS_BY_LANG[language] || LONG_MONTHS_BY_LANG.fr;
  const d = parseDateLocal(date);
  if (!d) return String(date);
  if (language === 'en') {
    return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
  }
  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
}

/**
 * Short-form localized month + year (e.g., "janv 2024" / "Jan 2024" / "ene 2024").
 * Used by the travel-history review screen.
 */
export function formatDateShortLocalized(date: Date, language: string = 'fr'): string {
  const months = SHORT_MONTHS_BY_LANG[language] || SHORT_MONTHS_BY_LANG.fr;
  return `${months[date.getMonth()]} ${date.getFullYear()}`;
}
